/**
 * Inspelning i webbläsaren för Möten.
 *
 * En enda MediaRecorder per inspelning med `timeslice` 60 s. Paus/fortsätt går
 * via pause()/resume() – aldrig omstart – så delarna bildar en giltig fil när de
 * läggs efter varandra (webm i Chromium/Firefox, fragmenterad mp4 i Safari).
 *
 * Delnumrering: 1, 2, 3 … Hooken anropar `ny_del` för VARJE del, även del 1, och
 * får då en färsk signerad uppladdning (upsert). Gränssnittet behöver alltså bara
 * skapa mötet (`skapa` med kalla 'inspelning' och `format`, som inte ger någon
 * uppladdningsadress) och skicka in `moteId`.
 *
 * ⚠️ Använd alltid `key={moteId}` på komponenten som anropar hooken. Kön,
 * delräknaren och recordern hör till ETT möte; byter `moteId` utan ny nyckel
 * hamnar delar från det gamla mötet i det nya.
 *
 * Uppladdningen är seriell och tappar aldrig en del: övergående fel (nät, 5xx,
 * 408, 429) ligger kvar i kön och försöks igen vid `online` och på en
 * backoff-timer. Permanenta fel (övriga 4xx) stoppar inspelningen med status
 * 'fel' – att försöka igen ger samma svar.
 *
 * Avbryter enheten inspelningen (samtal, låst skärm, mikrofonen tas bort) går
 * status till 'laddar_upp' och det som spelats in sparas via Avsluta.
 *
 * Stängs fliken kan inspelningen inte fortsätta; det som hunnit laddas upp tas
 * om hand av mötesvyn (`ljud_klar` utan `delar`).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { motenApi } from '../lib/api';
import { klassaUppladdningsfel, skapaKo } from '../lib/inspelningsKo';
import { supabase } from '../lib/supabase';

export type InspelningsStatus = 'redo' | 'spelar_in' | 'pausad' | 'laddar_upp' | 'klar' | 'fel';
export type LjudFormat = 'webm' | 'mp4';

export const TIMESLICE_MS = 60_000;
export const LJUD_BITAR_PER_SEKUND = 32_000;
export const MAX_DELAR = 1000;
const AVSLUTA_VANTA_MS = 60_000;
const STOPP_VANTA_MS = 5_000;
const MUTE_GRANS_MS = 3_000;
const BACKOFF_MS = [2_000, 4_000, 8_000, 16_000, 30_000];

const AVBRUTEN_TEXT =
  'Inspelningen avbröts av enheten (t.ex. ett samtal eller låst skärm). Det som spelats in hittills sparas – tryck Avsluta.';

/** Format ur recorderns faktiska mimeType ("audio/mp4", "audio/webm;codecs=opus" …). */
export function formatFranMime(mime: string): LjudFormat {
  return /mp4|m4a|aac/i.test(mime) ? 'mp4' : 'webm';
}

/** Första mimeType webbläsaren kan spela in; tom sträng = låt webbläsaren välja. */
export function valjInspelningsMime(): string {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const m of ['audio/webm;codecs=opus', 'audio/mp4']) {
    try {
      if (MediaRecorder.isTypeSupported(m)) return m;
    } catch {
      /* prova nästa */
    }
  }
  return '';
}

export interface Inspelning {
  status: InspelningsStatus;
  sekunder: number;
  vantandeDelar: number;
  /** Delar som spelats in hittills (0 = inget ljud har kommit in). */
  inspeladeDelar: number;
  /** Läsbar status- eller felrad, eller null. */
  meddelande: string | null;
  starta(): Promise<void>;
  pausa(): void;
  fortsatt(): void;
  avsluta(): Promise<void>;
}

interface Opt {
  /** Bara för prov: kortare timeslice. */
  timesliceMs?: number;
}

type WakeLockLike = { release(): Promise<void>; addEventListener?: (t: string, f: () => void) => void };

export function useInspelning(moteId: string, opt: Opt = {}): Inspelning {
  const timeslice = opt.timesliceMs ?? TIMESLICE_MS;
  const [status, setStatusState] = useState<InspelningsStatus>('redo');
  const [sekunder, setSekunder] = useState(0);
  const [vantandeDelar, setVantande] = useState(0);
  const [inspeladeDelar, setInspeladeDelar] = useState(0);
  const [meddelande, setMeddelande] = useState<string | null>(null);

  const statusRef = useRef<InspelningsStatus>('redo');
  const ko = useRef(skapaKo());
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const format = useRef<LjudFormat>('webm');
  const sistaNr = useRef(0);
  const pumpar = useRef(false);
  const forsok = useRef(0);
  const permanentFel = useRef<string | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const muteTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wake = useRef<WakeLockLike | null>(null);
  const wakeKo = useRef<Promise<void>>(Promise.resolve());
  const tickTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const segStart = useRef<number | null>(null);
  const ackSek = useRef(0);
  const lyssnare = useRef<Array<() => void>>([]);
  const avslutar = useRef(false);
  const startar = useRef(false);
  const avmonterad = useRef(false);

  const setStatus = useCallback((s: InspelningsStatus) => {
    statusRef.current = s;
    if (!avmonterad.current) setStatusState(s);
  }, []);

  const meddela = useCallback((t: string | null) => {
    if (!avmonterad.current) setMeddelande(t);
  }, []);

  const uppdateraVantande = useCallback(() => {
    if (!avmonterad.current) setVantande(ko.current.vantande());
    lyssnare.current.slice().forEach((f) => f());
  }, []);

  /* ---------- Wake Lock ---------- */

  // Kedjas så att två anrop aldrig begär varsitt lås, och ett lås som löser sig
  // efter att inspelningen slutat (eller hooken avmonterats) släpps direkt.
  const begarSkarmlas = useCallback(() => {
    wakeKo.current = wakeKo.current.then(async () => {
      const wl = (navigator as Navigator & { wakeLock?: { request(t: 'screen'): Promise<WakeLockLike> } }).wakeLock;
      if (!wl || wake.current) return;
      const onskad = () => !avmonterad.current && statusRef.current === 'spelar_in';
      if (!onskad()) return;
      try {
        const lock = await wl.request('screen');
        if (!onskad()) {
          void lock.release().catch(() => {});
          return;
        }
        wake.current = lock;
        lock.addEventListener?.('release', () => {
          if (wake.current === lock) wake.current = null;
        });
      } catch {
        /* stöds inte eller nekas – inget att visa */
      }
    });
  }, []);

  const slappSkarmlas = useCallback(() => {
    const lock = wake.current;
    wake.current = null;
    void lock?.release().catch(() => {});
  }, []);

  /* ---------- Tid ---------- */

  const stoppaTid = useCallback(() => {
    if (segStart.current !== null) {
      ackSek.current += (Date.now() - segStart.current) / 1000;
      segStart.current = null;
    }
    if (tickTimer.current) {
      clearInterval(tickTimer.current);
      tickTimer.current = null;
    }
    if (!avmonterad.current) setSekunder(Math.floor(ackSek.current));
  }, []);

  const startaTid = useCallback(() => {
    segStart.current = Date.now();
    if (tickTimer.current) clearInterval(tickTimer.current);
    tickTimer.current = setInterval(() => {
      const extra = segStart.current === null ? 0 : (Date.now() - segStart.current) / 1000;
      setSekunder(Math.floor(ackSek.current + extra));
    }, 500);
  }, []);

  /** Stoppar recorder, spår, tid och skärmlås. Slutlig dataavailable hinner köas. */
  const stangInspelning = useCallback(() => {
    const r = recorder.current;
    try {
      if (r && r.state !== 'inactive') r.stop();
    } catch {
      /* redan stoppad */
    }
    stream.current?.getTracks().forEach((t) => t.stop());
    if (muteTimer.current) {
      clearTimeout(muteTimer.current);
      muteTimer.current = null;
    }
    stoppaTid();
    slappSkarmlas();
  }, [slappSkarmlas, stoppaTid]);

  /** Enheten (inte användaren) avbröt inspelningen. */
  const avbrutet = useCallback(() => {
    const s = statusRef.current;
    if (s !== 'spelar_in' && s !== 'pausad') return;
    setStatus('laddar_upp');
    meddela(AVBRUTEN_TEXT);
    stangInspelning();
  }, [meddela, setStatus, stangInspelning]);

  /* ---------- Uppladdning ---------- */

  const pumpRef = useRef<() => void>(() => {});

  const planeraOmforsok = useCallback(() => {
    if (retryTimer.current) return;
    const ms = BACKOFF_MS[Math.min(forsok.current, BACKOFF_MS.length - 1)];
    forsok.current += 1;
    retryTimer.current = setTimeout(() => {
      retryTimer.current = null;
      pumpRef.current();
    }, ms);
  }, []);

  useEffect(() => {
    pumpRef.current = () => {
      if (pumpar.current || avmonterad.current || permanentFel.current) return;
      pumpar.current = true;
      void (async () => {
        try {
          for (;;) {
            const del = ko.current.nasta();
            if (!del) break;
            try {
              const r = await motenApi<{ uppladdning: { path: string; token: string } }>({
                atgard: 'ny_del',
                id: moteId,
                nr: del.nr,
                format: format.current,
              });
              const { error } = await supabase.storage
                .from('motesljud')
                .uploadToSignedUrl(r.uppladdning.path, r.uppladdning.token, del.blob, {
                  contentType: `audio/${format.current}`,
                });
              if (error) throw error;
              ko.current.klar(del.nr);
              forsok.current = 0;
              uppdateraVantande();
            } catch (e) {
              ko.current.misslyckad(del.nr);
              uppdateraVantande();
              const klass = klassaUppladdningsfel(e);
              if (klass.permanent) {
                permanentFel.current = klass.text;
                if (retryTimer.current) {
                  clearTimeout(retryTimer.current);
                  retryTimer.current = null;
                }
                stangInspelning();
                setStatus('fel');
                meddela(klass.text);
                uppdateraVantande(); // väcker en Avsluta som väntar
                break;
              }
              const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
              meddela(
                offline
                  ? 'Ingen uppkoppling – inspelningen sparas och laddas upp när nätet är tillbaka.'
                  : `${klass.text} (del ${del.nr})`,
              );
              planeraOmforsok();
              break;
            }
          }
        } finally {
          pumpar.current = false;
        }
      })();
    };
  }, [moteId, meddela, planeraOmforsok, setStatus, stangInspelning, uppdateraVantande]);

  useEffect(() => {
    const online = () => {
      if (retryTimer.current) {
        clearTimeout(retryTimer.current);
        retryTimer.current = null;
      }
      forsok.current = 0;
      pumpRef.current();
    };
    window.addEventListener('online', online);
    return () => window.removeEventListener('online', online);
  }, []);

  useEffect(() => {
    const synlig = () => {
      if (document.visibilityState === 'visible' && statusRef.current === 'spelar_in') begarSkarmlas();
    };
    document.addEventListener('visibilitychange', synlig);
    return () => document.removeEventListener('visibilitychange', synlig);
  }, [begarSkarmlas]);

  /* ---------- Varnar vid stängning ---------- */

  useEffect(() => {
    const f = (e: BeforeUnloadEvent) => {
      const s = statusRef.current;
      if (s === 'spelar_in' || s === 'pausad' || ko.current.vantande() > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', f);
    return () => window.removeEventListener('beforeunload', f);
  }, []);

  /* ---------- Städning ---------- */

  useEffect(() => {
    avmonterad.current = false;
    return () => {
      avmonterad.current = true;
      if (tickTimer.current) clearInterval(tickTimer.current);
      if (retryTimer.current) clearTimeout(retryTimer.current);
      if (muteTimer.current) clearTimeout(muteTimer.current);
      try {
        if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
      } catch {
        /* redan stoppad */
      }
      stream.current?.getTracks().forEach((t) => t.stop());
      slappSkarmlas();
    };
  }, [slappSkarmlas]);

  /* ---------- Styrning ---------- */

  const stoppaRecorder = useCallback(
    () =>
      new Promise<void>((resolve) => {
        const r = recorder.current;
        if (!r || r.state === 'inactive') return resolve();
        const t = setTimeout(resolve, STOPP_VANTA_MS);
        r.addEventListener(
          'stop',
          () => {
            clearTimeout(t);
            resolve();
          },
          { once: true },
        );
        try {
          r.stop();
        } catch {
          clearTimeout(t);
          resolve();
        }
      }),
    [],
  );

  const starta = useCallback(async () => {
    if (startar.current) return;
    if (statusRef.current !== 'redo' && !(statusRef.current === 'fel' && !recorder.current)) return;
    startar.current = true;
    try {
      if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        setStatus('fel');
        meddela('Den här webbläsaren kan inte spela in ljud. Ladda upp en ljudfil i stället.');
        return;
      }
      let s: MediaStream;
      try {
        s = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch {
        setStatus('fel');
        meddela('Mikrofonen kunde inte öppnas. Tillåt mikrofon i webbläsaren och försök igen.');
        return;
      }
      if (avmonterad.current) {
        s.getTracks().forEach((t) => t.stop());
        return;
      }
      stream.current = s;
      const mime = valjInspelningsMime();
      let r: MediaRecorder;
      try {
        r = new MediaRecorder(s, {
          ...(mime ? { mimeType: mime } : {}),
          audioBitsPerSecond: LJUD_BITAR_PER_SEKUND,
        });
      } catch {
        s.getTracks().forEach((t) => t.stop());
        setStatus('fel');
        meddela('Inspelningen kunde inte startas i den här webbläsaren.');
        return;
      }
      format.current = formatFranMime(r.mimeType || mime);
      recorder.current = r;

      r.addEventListener('dataavailable', (e: BlobEvent) => {
        if (!e.data || e.data.size === 0) return;
        if (sistaNr.current >= MAX_DELAR) return;
        sistaNr.current += 1;
        setInspeladeDelar(sistaNr.current);
        ko.current.lagg(sistaNr.current, new Blob([e.data], { type: `audio/${format.current}` }));
        uppdateraVantande();
        pumpRef.current();
        // Stoppa vid näst sista delen: stop() spolar ut det som är kvar i bufferten som en
        // sista dataavailable, och den ska bli del MAX_DELAR – inte stoppas av spärren ovan.
        if (sistaNr.current >= MAX_DELAR - 1 && (statusRef.current === 'spelar_in' || statusRef.current === 'pausad')) {
          setStatus('laddar_upp');
          meddela('Inspelningen nådde maxlängden (ungefär 16 timmar) och har stoppats. Avsluta för att spara den.');
          stangInspelning();
        }
      });
      // Stopp som inte kommer från Avsluta (då är status redan 'laddar_upp').
      r.addEventListener('stop', avbrutet);
      r.addEventListener('error', avbrutet);
      s.getAudioTracks().forEach((t) => {
        t.addEventListener('ended', avbrutet);
        t.addEventListener('mute', () => {
          if (muteTimer.current) clearTimeout(muteTimer.current);
          muteTimer.current = setTimeout(() => {
            muteTimer.current = null;
            avbrutet();
          }, MUTE_GRANS_MS);
        });
        t.addEventListener('unmute', () => {
          if (muteTimer.current) {
            clearTimeout(muteTimer.current);
            muteTimer.current = null;
          }
        });
      });

      ackSek.current = 0;
      setSekunder(0);
      r.start(timeslice);
      startaTid();
      setStatus('spelar_in');
      meddela(null);
      begarSkarmlas();
    } finally {
      startar.current = false;
    }
  }, [avbrutet, begarSkarmlas, meddela, setStatus, startaTid, stangInspelning, timeslice, uppdateraVantande]);

  const pausa = useCallback(() => {
    const r = recorder.current;
    if (!r || statusRef.current !== 'spelar_in' || r.state !== 'recording') return;
    r.pause();
    stoppaTid();
    setStatus('pausad');
    slappSkarmlas();
  }, [setStatus, slappSkarmlas, stoppaTid]);

  const fortsatt = useCallback(() => {
    const r = recorder.current;
    if (!r || statusRef.current !== 'pausad' || r.state !== 'paused') return;
    r.resume();
    startaTid();
    setStatus('spelar_in');
    begarSkarmlas();
  }, [begarSkarmlas, setStatus, startaTid]);

  const avsluta = useCallback(async () => {
    if (avslutar.current) return;
    const s = statusRef.current;
    if (s === 'redo' || s === 'klar') return;
    if (permanentFel.current) {
      setStatus('fel');
      meddela(permanentFel.current);
      return;
    }
    avslutar.current = true;
    try {
      setStatus('laddar_upp');
      meddela(null);
      stoppaTid();
      await stoppaRecorder();
      stream.current?.getTracks().forEach((t) => t.stop());
      slappSkarmlas();
      pumpRef.current();

      // Vänta tills kön är tom, högst 60 s.
      await new Promise<void>((resolve) => {
        if (ko.current.vantande() === 0) return resolve();
        const kolla = () => {
          if (ko.current.vantande() === 0 || permanentFel.current) slut();
        };
        const t = setTimeout(() => slut(), AVSLUTA_VANTA_MS);
        function slut() {
          clearTimeout(t);
          lyssnare.current = lyssnare.current.filter((f) => f !== kolla);
          resolve();
        }
        lyssnare.current.push(kolla);
      });

      if (permanentFel.current) return; // status och meddelande satta av pumpen
      const kvar = ko.current.vantande();
      if (kvar > 0) {
        meddela(
          `${kvar} ${kvar === 1 ? 'del' : 'delar'} har inte laddats upp än. Håll sidan öppen och tryck Avsluta igen när uppkopplingen är tillbaka.`,
        );
        return;
      }
      if (sistaNr.current === 0) {
        setStatus('fel');
        meddela('Ingen inspelning att spara.');
        return;
      }
      try {
        await motenApi({
          atgard: 'ljud_klar',
          id: moteId,
          format: format.current,
          delar: sistaNr.current,
        });
        setStatus('klar');
        meddela(null);
      } catch (e) {
        setStatus('fel');
        meddela((e as Error).message);
      }
    } finally {
      avslutar.current = false;
    }
  }, [meddela, moteId, setStatus, slappSkarmlas, stoppaRecorder, stoppaTid]);

  return { status, sekunder, vantandeDelar, inspeladeDelar, meddelande, starta, pausa, fortsatt, avsluta };
}
